import React, { useState, useEffect } from 'react';
import { Card } from '../ui/card';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Badge } from '../ui/badge';
import { Textarea } from '../ui/textarea';
import { Label } from '../ui/label';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from '../ui/dialog';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../ui/select';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../ui/tabs';
import { toast } from 'sonner';
import { adminAPI } from '../../lib/api';
import {
  Search, DollarSign, CreditCard, FileText, Send, Clock,
  CheckCircle, XCircle, Edit, Eye, Copy, ExternalLink, Plus,
  Phone, Mail, MessageSquare, RefreshCw, Calendar, Users,
  AlertCircle, Loader2, ArrowRight, Receipt, Filter, Download,
  TrendingUp, Banknote, Smartphone, X, BarChart3
} from 'lucide-react';

const formatPrice = (price) => {
  return new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency: 'USD'
  }).format(price || 0);
};

const formatDate = (dateStr) => {
  if (!dateStr) return 'N/A';
  const date = new Date(dateStr);
  return date.toLocaleDateString('en-US', { 
    month: 'short', 
    day: 'numeric',
    year: 'numeric'
  });
};

const formatDateTime = (dateStr) => {
  if (!dateStr) return 'N/A';
  const date = new Date(dateStr);
  return date.toLocaleString('en-US', { 
    month: 'short', 
    day: 'numeric',
    year: 'numeric',
    hour: 'numeric',
    minute: '2-digit'
  });
};

const PAYMENT_METHODS = [
  { value: 'stripe', label: 'Stripe', icon: CreditCard, color: 'bg-purple-100 text-purple-700' },
  { value: 'cash', label: 'Cash', icon: Banknote, color: 'bg-green-100 text-green-700' },
  { value: 'cashapp', label: 'CashApp', icon: Smartphone, color: 'bg-emerald-100 text-emerald-700' },
  { value: 'zelle', label: 'Zelle', icon: Smartphone, color: 'bg-violet-100 text-violet-700' },
  { value: 'applepay', label: 'Apple Pay', icon: Smartphone, color: 'bg-gray-100 text-gray-700' },
];

export default function EnhancedPaymentsTab({ onRefresh }) {
  const [activeSubTab, setActiveSubTab] = useState('history');
  const [paymentHistory, setPaymentHistory] = useState([]);
  const [manualPayments, setManualPayments] = useState([]);
  const [invoices, setInvoices] = useState([]);
  const [tours, setTours] = useState([]);
  const [bookings, setBookings] = useState([]);
  const [refundLogs, setRefundLogs] = useState([]);
  const [cancellationLogs, setCancellationLogs] = useState([]);
  const [loading, setLoading] = useState(true);
  
  // Filter states
  const [searchTerm, setSearchTerm] = useState('');
  const [filterStatus, setFilterStatus] = useState('all');
  const [filterMethod, setFilterMethod] = useState('all');
  const [filterDateFrom, setFilterDateFrom] = useState('');
  const [filterDateTo, setFilterDateTo] = useState('');
  const [filterTour, setFilterTour] = useState('all');
  
  // Dialog states
  const [showQuickPayment, setShowQuickPayment] = useState(false);
  const [showCreateInvoice, setShowCreateInvoice] = useState(false);
  const [showManualPayment, setShowManualPayment] = useState(false);
  const [showProfitReport, setShowProfitReport] = useState(false);
  const [showRefundDialog, setShowRefundDialog] = useState(false);
  const [showPaymentDetails, setShowPaymentDetails] = useState(null);
  const [showEditMessage, setShowEditMessage] = useState(null);
  
  // Form states
  const [quickPaymentForm, setQuickPaymentForm] = useState({
    customer_name: '', customer_email: '', customer_phone: '',
    tour_id: '', amount: '', guests: 1, tour_date: '', notes: ''
  });
  const [invoiceForm, setInvoiceForm] = useState({
    customer_name: '', customer_email: '', customer_phone: '',
    tour_id: '', amount: '', guests: 1, tour_date: '', due_date: '', notes: ''
  });
  const [manualPaymentForm, setManualPaymentForm] = useState({
    customer_name: '', customer_email: '', customer_phone: '',
    tour_id: '', amount: '', guests: 1, tour_date: '',
    payment_method: 'cash', reference_number: '', notes: ''
  });
  const [messageContent, setMessageContent] = useState({ subject: '', body: '' });
  const [refundItem, setRefundItem] = useState(null);
  
  // Report states
  const [reportFilters, setReportFilters] = useState({
    dateFrom: '', dateTo: '', tourId: 'all'
  });
  const [reportData, setReportData] = useState(null);

  useEffect(() => {
    loadData();
  }, []);

  const loadData = async () => {
    setLoading(true);
    try {
      const [histRes, invRes, toursRes, bookingsRes, refundsRes, cancelsRes] = await Promise.all([
        adminAPI.getPaymentHistory(),
        adminAPI.getAllInvoices(),
        adminAPI.getAllTours(),
        adminAPI.getAllBookings(),
        adminAPI.getRefundLogs().catch(() => ({ data: [] })),
        adminAPI.getCancellationHistory().catch(() => ({ data: [] }))
      ]);
      setPaymentHistory(histRes.data || []);
      setInvoices(invRes.data || []);
      setTours(toursRes.data || []);
      setBookings(bookingsRes.data || []);
      setRefundLogs(refundsRes.data || []);
      setCancellationLogs(cancelsRes.data || []);
      
      // Load manual payments from localStorage (or backend if implemented)
      const stored = localStorage.getItem('manual_payments');
      if (stored) setManualPayments(JSON.parse(stored));
    } catch (error) {
      console.error('Error loading data:', error);
      toast.error('Failed to load payment data');
    } finally {
      setLoading(false);
    }
  };

  // Combined payment history (Stripe + Manual + Bookings)
  const getAllPayments = () => {
    const stripePayments = paymentHistory.map(p => ({ ...p, payment_method: 'stripe' }));
    
    // Convert bookings to payment format for display
    const bookingPayments = bookings.map(b => ({
      id: b.id,
      customer_name: b.booker_name || b.user_email?.split('@')[0] || 'Guest',
      customer_email: b.booker_email || b.user_email,
      customer_phone: b.booker_phone,
      tour_id: b.tour_id,
      tour_title: b.tour_title,
      tour_date: b.date,
      guests: b.guests,
      amount: b.total_price,
      total_price: b.total_price,
      payment_status: b.payment_status,
      payment_method: 'stripe',
      booking_id: b.id,
      created_at: b.created_at,
      paid_at: b.payment_status === 'paid' ? b.updated_at || b.created_at : null,
      status: b.status
    }));
    
    // Combine all payment sources, avoid duplicates by ID and booking_id
    const seenIds = new Set();
    const seenBookingIds = new Set();
    const combined = [];
    
    [...bookingPayments, ...stripePayments, ...manualPayments].forEach(p => {
      // Skip if we've seen this exact ID
      if (seenIds.has(p.id)) return;
      
      // Skip if this booking_id was already processed
      if (p.booking_id && seenBookingIds.has(p.booking_id)) return;
      
      // Skip entries with invalid/empty customer names or N/A amounts
      const customerName = p.customer_name?.trim();
      if (!customerName || customerName === 'N/A' || customerName === 'undefined') return;
      
      // Skip if amount is invalid
      const amount = p.amount || p.total_price;
      if (!amount || amount <= 0 || isNaN(amount)) return;
      
      seenIds.add(p.id);
      if (p.booking_id) seenBookingIds.add(p.booking_id);
      combined.push(p);
    });
    
    return combined.sort((a, b) => 
      new Date(b.created_at || b.paid_at) - new Date(a.created_at || a.paid_at)
    );
  };

  // Apply filters
  const applyFilters = (items) => {
    return items.filter(item => {
      // Search filter
      if (searchTerm) {
        const search = searchTerm.toLowerCase();
        const matchesSearch = 
          item.customer_name?.toLowerCase().includes(search) ||
          item.customer_email?.toLowerCase().includes(search) ||
          item.tour_title?.toLowerCase().includes(search) ||
          item.booking_id?.toLowerCase().includes(search);
        if (!matchesSearch) return false;
      }
      
      // Status filter
      if (filterStatus !== 'all' && item.payment_status !== filterStatus) return false;
      
      // Payment method filter
      if (filterMethod !== 'all' && item.payment_method !== filterMethod) return false;
      
      // Tour filter
      if (filterTour !== 'all' && item.tour_id !== filterTour) return false;
      
      // Date range filter
      if (filterDateFrom) {
        const itemDate = new Date(item.created_at || item.paid_at);
        if (itemDate < new Date(filterDateFrom)) return false;
      }
      if (filterDateTo) {
        const itemDate = new Date(item.created_at || item.paid_at);
        if (itemDate > new Date(filterDateTo + 'T23:59:59')) return false;
      }
      
      return true;
    });
  };

  const filteredPayments = applyFilters(getAllPayments());
  const filteredInvoices = applyFilters(invoices);

  // Stats
  const stats = {
    totalRevenue: filteredPayments.filter(p => p.payment_status === 'paid').reduce((sum, p) => sum + (p.amount || p.total_price || 0), 0),
    pendingPayments: filteredPayments.filter(p => p.payment_status === 'pending').length,
    completedToday: filteredPayments.filter(p => {
      const paidDate = new Date(p.paid_at || p.created_at);
      return p.payment_status === 'paid' && paidDate.toDateString() === new Date().toDateString();
    }).length,
    totalTransactions: filteredPayments.length,
    totalRefunds: refundLogs.reduce((sum, r) => sum + (r.amount || 0), 0),
    refundCount: refundLogs.length,
    totalCancellations: cancellationLogs.length,
    cancellationValue: cancellationLogs.reduce((sum, c) => sum + (c.original_amount || 0), 0)
  };

  // Quick Payment - Opens Stripe
  const handleQuickPayment = async () => {
    if (!quickPaymentForm.customer_name || !quickPaymentForm.amount) {
      toast.error('Please fill customer name and amount');
      return;
    }

    try {
      const tour = tours.find(t => t.id === quickPaymentForm.tour_id);
      const response = await adminAPI.createPOSPayment({
        ...quickPaymentForm,
        tour_title: tour?.title || 'Custom Payment',
        amount: parseFloat(quickPaymentForm.amount)
      });

      if (response.data.checkout_url) {
        toast.success('Opening Stripe checkout...');
        window.open(response.data.checkout_url, '_blank');
      }

      setShowQuickPayment(false);
      setQuickPaymentForm({
        customer_name: '', customer_email: '', customer_phone: '',
        tour_id: '', amount: '', guests: 1, tour_date: '', notes: ''
      });
      loadData();
    } catch (error) {
      toast.error('Failed to create payment');
    }
  };

  // Create Invoice
  const handleCreateInvoice = async (sendEmail = false) => {
    if (!invoiceForm.customer_name || !invoiceForm.amount || !invoiceForm.customer_email) {
      toast.error('Please fill required fields');
      return;
    }

    try {
      const tour = tours.find(t => t.id === invoiceForm.tour_id);
      const invoiceData = {
        ...invoiceForm,
        tour_title: tour?.title || 'Custom Invoice',
        amount: parseFloat(invoiceForm.amount)
      };
      
      const response = await adminAPI.createInvoice(invoiceData);
      const createdInvoice = response.data;
      
      toast.success('Invoice created');
      
      if (sendEmail && invoiceForm.customer_email) {
        // Generate and send invoice email
        const invoiceNumber = createdInvoice?.invoice_number || `INV-${Date.now().toString().slice(-8)}`;
        const emailSubject = `Invoice #${invoiceNumber} - ${invoiceData.tour_title}`;
        const emailBody = `Dear ${invoiceForm.customer_name},

Please find below your invoice details:

Invoice #: ${invoiceNumber}
Tour: ${invoiceData.tour_title}
Amount Due: ${formatPrice(invoiceData.amount)}
${invoiceForm.tour_date ? `Tour Date: ${formatDate(invoiceForm.tour_date)}` : ''}
${invoiceForm.due_date ? `Due Date: ${formatDate(invoiceForm.due_date)}` : ''}
${invoiceForm.guests > 1 ? `Guests: ${invoiceForm.guests}` : ''}
${invoiceForm.notes ? `Notes: ${invoiceForm.notes}` : ''}

${createdInvoice?.payment_url ? `Pay now: ${createdInvoice.payment_url}` : ''}

Thank you for choosing Tour Finder!

Best regards,
Tour Finder Team`;

        try {
          await adminAPI.sendCustomerEmail({
            to_email: invoiceForm.customer_email,
            subject: emailSubject,
            body: emailBody,
            customer_name: invoiceForm.customer_name
          });
          toast.success('Invoice email sent!');
        } catch (emailError) {
          toast.error('Invoice created but email failed to send');
          console.error('Email error:', emailError);
        }
      }
      
      setShowCreateInvoice(false);
      setInvoiceForm({
        customer_name: '', customer_email: '', customer_phone: '',
        tour_id: '', amount: '', guests: 1, tour_date: '', due_date: '', notes: ''
      });
      loadData();
    } catch (error) {
      toast.error('Failed to create invoice');
    }
  };

  // Add Manual Payment
  const handleAddManualPayment = () => {
    if (!manualPaymentForm.customer_name || !manualPaymentForm.amount) {
      toast.error('Please fill customer name and amount');
      return;
    }

    const tour = tours.find(t => t.id === manualPaymentForm.tour_id);
    const newPayment = {
      id: `manual_${Date.now()}`,
      ...manualPaymentForm,
      tour_title: tour?.title || 'Custom Payment',
      amount: parseFloat(manualPaymentForm.amount),
      payment_status: 'paid',
      created_at: new Date().toISOString(),
      paid_at: new Date().toISOString()
    };

    const updated = [...manualPayments, newPayment];
    setManualPayments(updated);
    localStorage.setItem('manual_payments', JSON.stringify(updated));

    toast.success(`${PAYMENT_METHODS.find(m => m.value === manualPaymentForm.payment_method)?.label} payment recorded`);
    setShowManualPayment(false);
    setManualPaymentForm({
      customer_name: '', customer_email: '', customer_phone: '',
      tour_id: '', amount: '', guests: 1, tour_date: '',
      payment_method: 'cash', reference_number: '', notes: ''
    });
  };

  // Generate Profit Report
  const generateProfitReport = () => {
    let reportPayments = getAllPayments().filter(p => p.payment_status === 'paid');
    
    if (reportFilters.dateFrom) {
      reportPayments = reportPayments.filter(p => 
        new Date(p.paid_at || p.created_at) >= new Date(reportFilters.dateFrom)
      );
    }
    if (reportFilters.dateTo) {
      reportPayments = reportPayments.filter(p => 
        new Date(p.paid_at || p.created_at) <= new Date(reportFilters.dateTo + 'T23:59:59')
      );
    }
    if (reportFilters.tourId !== 'all') {
      reportPayments = reportPayments.filter(p => p.tour_id === reportFilters.tourId);
    }

    const byMethod = {};
    PAYMENT_METHODS.forEach(m => { byMethod[m.value] = 0; });
    
    const byTour = {};
    let totalRevenue = 0;
    let totalTransactions = 0;

    reportPayments.forEach(p => {
      const amount = p.amount || p.total_price || 0;
      totalRevenue += amount;
      totalTransactions++;
      
      const method = p.payment_method || 'stripe';
      byMethod[method] = (byMethod[method] || 0) + amount;
      
      const tour = p.tour_title || 'Other';
      byTour[tour] = (byTour[tour] || 0) + amount;
    });

    // Get cancellations
    const cancellations = bookings.filter(b => {
      const cancelDate = new Date(b.cancelled_at || b.updated_at);
      const inRange = (!reportFilters.dateFrom || cancelDate >= new Date(reportFilters.dateFrom)) &&
                      (!reportFilters.dateTo || cancelDate <= new Date(reportFilters.dateTo + 'T23:59:59'));
      return b.status === 'cancelled' && inRange;
    });

    setReportData({
      totalRevenue,
      totalTransactions,
      byMethod,
      byTour,
      cancellations: cancellations.length,
      cancellationValue: cancellations.reduce((sum, b) => sum + (b.total_price || 0), 0),
      dateRange: {
        from: reportFilters.dateFrom || 'All time',
        to: reportFilters.dateTo || 'Present'
      }
    });
  };

  // Open Stripe for Refund
  const handleRefund = (payment) => {
    setRefundItem(payment);
    setShowRefundDialog(true);
  };

  const openStripeRefund = () => {
    // Open Stripe dashboard payments page
    window.open('https://dashboard.stripe.com/test/payments', '_blank');
    toast.info('Opening Stripe Dashboard for refund processing');
    setShowRefundDialog(false);
  };

  // Edit message before sending
  const handleEditBeforeSend = (type, item) => {
    if (type === 'email') {
      setMessageContent({
        type: 'email',
        item,
        subject: `Payment Confirmation - ${item.tour_title || 'Your Booking'}`,
        body: `Dear ${item.customer_name},\n\nThank you for your payment of ${formatPrice(item.amount || item.total_price)}.\n\nTour: ${item.tour_title}\nDate: ${formatDate(item.tour_date)}\n\nWe look forward to seeing you!\n\nBest regards,\nTour Finder Team`
      });
    } else {
      setMessageContent({
        type: 'sms',
        item,
        subject: '',
        body: `Hi ${item.customer_name}, your payment of ${formatPrice(item.amount || item.total_price)} for ${item.tour_title} has been received. See you on ${formatDate(item.tour_date)}!`
      });
    }
    setShowEditMessage(true);
  };

  const sendEditedMessage = async () => {
    try {
      if (messageContent.type === 'email') {
        await adminAPI.sendCustomerEmail({
          to_email: messageContent.item.customer_email,
          subject: messageContent.subject,
          body: messageContent.body,
          customer_name: messageContent.item.customer_name
        });
        toast.success('Email sent!');
      } else {
        await adminAPI.sendCustomSMS({
          to_phone: messageContent.item.customer_phone,
          message: messageContent.body
        });
        toast.success('SMS sent!');
      }
      setShowEditMessage(false);
    } catch (error) {
      toast.error(`Failed to send ${messageContent.type}`);
    }
  };

  // Clear filters
  const clearFilters = () => {
    setSearchTerm('');
    setFilterStatus('all');
    setFilterMethod('all');
    setFilterDateFrom('');
    setFilterDateTo('');
    setFilterTour('all');
  };

  const hasActiveFilters = searchTerm || filterStatus !== 'all' || filterMethod !== 'all' || 
                           filterDateFrom || filterDateTo || filterTour !== 'all';

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <Loader2 className="h-8 w-8 animate-spin text-primary" />
      </div>
    );
  }

  return (
    <div data-testid="enhanced-payments-tab" className="space-y-6">
      {/* Header with Actions */}
      <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
        <div>
          <h1 className="font-heading text-3xl font-bold">Payments</h1>
          <p className="text-muted-foreground">Manage payments, invoices, and financial reports</p>
        </div>
        <div className="flex flex-wrap gap-2">
          <Button onClick={() => setShowQuickPayment(true)} className="gap-2">
            <CreditCard className="h-4 w-4" />
            Quick Payment
          </Button>
          <Button onClick={() => setShowManualPayment(true)} variant="outline" className="gap-2">
            <Banknote className="h-4 w-4" />
            Record Payment
          </Button>
          <Button onClick={() => setShowProfitReport(true)} variant="outline" className="gap-2">
            <BarChart3 className="h-4 w-4" />
            Profit Report
          </Button>
        </div>
      </div>

      {/* Stats Cards */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        <Card className="p-4">
          <div className="flex items-center gap-3">
            <div className="p-2 bg-green-100 rounded-lg">
              <DollarSign className="h-5 w-5 text-green-600" />
            </div>
            <div>
              <p className="text-2xl font-bold">{formatPrice(stats.totalRevenue)}</p>
              <p className="text-sm text-muted-foreground">Total Revenue</p>
            </div>
          </div>
        </Card>
        <Card className="p-4">
          <div className="flex items-center gap-3">
            <div className="p-2 bg-amber-100 rounded-lg">
              <Clock className="h-5 w-5 text-amber-600" />
            </div>
            <div>
              <p className="text-2xl font-bold">{stats.pendingPayments}</p>
              <p className="text-sm text-muted-foreground">Pending</p>
            </div>
          </div>
        </Card>
        <Card className="p-4">
          <div className="flex items-center gap-3">
            <div className="p-2 bg-blue-100 rounded-lg">
              <CheckCircle className="h-5 w-5 text-blue-600" />
            </div>
            <div>
              <p className="text-2xl font-bold">{stats.completedToday}</p>
              <p className="text-sm text-muted-foreground">Paid Today</p>
            </div>
          </div>
        </Card>
        <Card className="p-4">
          <div className="flex items-center gap-3">
            <div className="p-2 bg-purple-100 rounded-lg">
              <Receipt className="h-5 w-5 text-purple-600" />
            </div>
            <div>
              <p className="text-2xl font-bold">{stats.totalTransactions}</p>
              <p className="text-sm text-muted-foreground">Transactions</p>
            </div>
          </div>
        </Card>
      </div>

      {/* Tabs */}
      <Tabs value={activeSubTab} onValueChange={setActiveSubTab}>
        <div className="flex items-center justify-between">
          <TabsList>
            <TabsTrigger value="history" className="gap-2">
              <Receipt className="h-4 w-4" />
              Payment History
            </TabsTrigger>
            <TabsTrigger value="invoices" className="gap-2">
              <FileText className="h-4 w-4" />
              Invoices
            </TabsTrigger>
            <TabsTrigger value="refunds" className="gap-2">
              <RefreshCw className="h-4 w-4" />
              Refunds & Cancellations
            </TabsTrigger>
          </TabsList>
          
          {activeSubTab === 'invoices' && (
            <Button onClick={() => setShowCreateInvoice(true)} size="sm" className="gap-2">
              <Plus className="h-4 w-4" />
              New Invoice
            </Button>
          )}
        </div>

        {/* Filters */}
        <Card className="p-4 mt-4">
          <div className="flex flex-wrap items-center gap-3">
            <div className="flex items-center gap-2">
              <Filter className="h-4 w-4 text-muted-foreground" />
              <span className="text-sm font-medium">Filters:</span>
            </div>
            
            <div className="relative">
              <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-muted-foreground" />
              <Input
                placeholder="Search..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="pl-9 w-48"
              />
            </div>

            <Select value={filterStatus} onValueChange={setFilterStatus}>
              <SelectTrigger className="w-32">
                <SelectValue placeholder="Status" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Status</SelectItem>
                <SelectItem value="paid">Paid</SelectItem>
                <SelectItem value="pending">Pending</SelectItem>
                <SelectItem value="refunded">Refunded</SelectItem>
              </SelectContent>
            </Select>

            <Select value={filterMethod} onValueChange={setFilterMethod}>
              <SelectTrigger className="w-36">
                <SelectValue placeholder="Method" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Methods</SelectItem>
                {PAYMENT_METHODS.map(m => (
                  <SelectItem key={m.value} value={m.value}>{m.label}</SelectItem>
                ))}
              </SelectContent>
            </Select>

            <Select value={filterTour} onValueChange={setFilterTour}>
              <SelectTrigger className="w-40">
                <SelectValue placeholder="Tour" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Tours</SelectItem>
                {tours.map(t => (
                  <SelectItem key={t.id} value={t.id}>{t.title}</SelectItem>
                ))}
              </SelectContent>
            </Select>

            <Input
              type="date"
              value={filterDateFrom}
              onChange={(e) => setFilterDateFrom(e.target.value)}
              className="w-36"
              placeholder="From"
            />
            <Input
              type="date"
              value={filterDateTo}
              onChange={(e) => setFilterDateTo(e.target.value)}
              className="w-36"
              placeholder="To"
            />

            {hasActiveFilters && (
              <Button variant="ghost" size="sm" onClick={clearFilters} className="gap-1">
                <X className="h-4 w-4" />
                Clear
              </Button>
            )}
          </div>
          
          {/* Results count */}
          <div className="mt-3 text-sm text-muted-foreground">
            Showing <span className="font-semibold text-foreground">
              {activeSubTab === 'history' ? filteredPayments.length : filteredInvoices.length}
            </span> results
            {hasActiveFilters && ' (filtered)'}
          </div>
        </Card>

        {/* Payment History Tab */}
        <TabsContent value="history" className="mt-4">
          <Card className="rounded-xl">
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead className="bg-muted/50">
                  <tr>
                    <th className="text-left p-4 font-semibold">Customer</th>
                    <th className="text-left p-4 font-semibold">Tour</th>
                    <th className="text-left p-4 font-semibold">Date</th>
                    <th className="text-left p-4 font-semibold">Amount</th>
                    <th className="text-left p-4 font-semibold">Method</th>
                    <th className="text-left p-4 font-semibold">Status</th>
                    <th className="text-right p-4 font-semibold">Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {filteredPayments.length === 0 ? (
                    <tr>
                      <td colSpan={7} className="p-8 text-center text-muted-foreground">
                        No payments found
                      </td>
                    </tr>
                  ) : (
                    filteredPayments.map((payment) => {
                      const method = PAYMENT_METHODS.find(m => m.value === (payment.payment_method || 'stripe'));
                      return (
                        <tr key={payment.id} className="border-t hover:bg-muted/30">
                          <td className="p-4">
                            <div className="font-medium">{payment.customer_name || payment.booker_name || 'N/A'}</div>
                            <div className="text-sm text-muted-foreground">{payment.customer_email || payment.booker_email}</div>
                          </td>
                          <td className="p-4">
                            <div>{payment.tour_title}</div>
                            <div className="text-sm text-muted-foreground">{payment.guests} guests</div>
                          </td>
                          <td className="p-4">
                            <div>{formatDate(payment.tour_date || payment.date)}</div>
                            <div className="text-xs text-muted-foreground">{formatDateTime(payment.paid_at || payment.created_at)}</div>
                          </td>
                          <td className="p-4 font-semibold text-lg">
                            {formatPrice(payment.amount || payment.total_price)}
                          </td>
                          <td className="p-4">
                            <Badge className={method?.color}>
                              {method?.label || 'Stripe'}
                            </Badge>
                          </td>
                          <td className="p-4">
                            <Badge variant={payment.payment_status === 'paid' ? 'default' : payment.payment_status === 'refunded' ? 'destructive' : 'secondary'}>
                              {payment.payment_status}
                            </Badge>
                          </td>
                          <td className="p-4">
                            <div className="flex justify-end gap-1">
                              <Button
                                size="sm"
                                variant="ghost"
                                onClick={() => setShowPaymentDetails(payment)}
                                title="View Details"
                              >
                                <Eye className="h-4 w-4" />
                              </Button>
                              {payment.customer_email && (
                                <Button
                                  size="sm"
                                  variant="ghost"
                                  onClick={() => handleEditBeforeSend('email', payment)}
                                  title="Send Email"
                                >
                                  <Mail className="h-4 w-4" />
                                </Button>
                              )}
                              {payment.customer_phone && (
                                <Button
                                  size="sm"
                                  variant="ghost"
                                  onClick={() => handleEditBeforeSend('sms', payment)}
                                  title="Send SMS"
                                >
                                  <MessageSquare className="h-4 w-4" />
                                </Button>
                              )}
                              {payment.payment_status === 'paid' && payment.payment_method === 'stripe' && (
                                <Button
                                  size="sm"
                                  variant="ghost"
                                  onClick={() => handleRefund(payment)}
                                  title="Process Refund"
                                  className="text-red-600 hover:text-red-700"
                                >
                                  <XCircle className="h-4 w-4" />
                                </Button>
                              )}
                            </div>
                          </td>
                        </tr>
                      );
                    })
                  )}
                </tbody>
              </table>
            </div>
          </Card>
        </TabsContent>

        {/* Invoices Tab */}
        <TabsContent value="invoices" className="mt-4">
          <Card className="rounded-xl">
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead className="bg-muted/50">
                  <tr>
                    <th className="text-left p-4 font-semibold">Invoice #</th>
                    <th className="text-left p-4 font-semibold">Customer</th>
                    <th className="text-left p-4 font-semibold">Amount</th>
                    <th className="text-left p-4 font-semibold">Due Date</th>
                    <th className="text-left p-4 font-semibold">Status</th>
                    <th className="text-right p-4 font-semibold">Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {filteredInvoices.length === 0 ? (
                    <tr>
                      <td colSpan={6} className="p-8 text-center text-muted-foreground">
                        No invoices found
                      </td>
                    </tr>
                  ) : (
                    filteredInvoices.map((invoice) => (
                      <tr key={invoice.id} className="border-t hover:bg-muted/30">
                        <td className="p-4 font-mono">{invoice.invoice_number || `INV-${invoice.id.slice(0,8)}`}</td>
                        <td className="p-4">
                          <div className="font-medium">{invoice.customer_name}</div>
                          <div className="text-sm text-muted-foreground">{invoice.customer_email}</div>
                        </td>
                        <td className="p-4 font-semibold">{formatPrice(invoice.amount)}</td>
                        <td className="p-4">{formatDate(invoice.due_date)}</td>
                        <td className="p-4">
                          <Badge variant={invoice.payment_status === 'paid' ? 'default' : 'secondary'}>
                            {invoice.payment_status}
                          </Badge>
                        </td>
                        <td className="p-4">
                          <div className="flex justify-end gap-1">
                            <Button
                              size="sm"
                              variant="default"
                              onClick={() => handleEditBeforeSend('email', invoice)}
                              title="Email Invoice"
                              className="gap-1"
                            >
                              <Send className="h-4 w-4" />
                              Email
                            </Button>
                            {invoice.customer_phone && (
                              <Button
                                size="sm"
                                variant="ghost"
                                onClick={() => handleEditBeforeSend('sms', invoice)}
                                title="Send SMS"
                              >
                                <MessageSquare className="h-4 w-4" />
                              </Button>
                            )}
                            {invoice.payment_url && (
                              <Button
                                size="sm"
                                variant="ghost"
                                onClick={() => window.open(invoice.payment_url, '_blank')}
                                title="Open Payment Link"
                              >
                                <ExternalLink className="h-4 w-4" />
                              </Button>
                            )}
                          </div>
                        </td>
                      </tr>
                    ))
                  )}
                </tbody>
              </table>
            </div>
          </Card>
        </TabsContent>

        {/* Refunds & Cancellations Tab */}
        <TabsContent value="refunds" className="mt-4">
          <div className="space-y-6">
            {/* Refunds & Cancellations Stats */}
            <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
              <Card className="p-4">
                <div className="flex items-center gap-3">
                  <div className="p-2 bg-amber-100 rounded-lg">
                    <RefreshCw className="h-5 w-5 text-amber-600" />
                  </div>
                  <div>
                    <p className="text-sm text-muted-foreground">Total Refunds</p>
                    <p className="text-2xl font-bold text-amber-600">{formatPrice(stats.totalRefunds)}</p>
                    <p className="text-xs text-muted-foreground">{stats.refundCount} refund(s)</p>
                  </div>
                </div>
              </Card>
              <Card className="p-4">
                <div className="flex items-center gap-3">
                  <div className="p-2 bg-red-100 rounded-lg">
                    <XCircle className="h-5 w-5 text-red-600" />
                  </div>
                  <div>
                    <p className="text-sm text-muted-foreground">Cancellations</p>
                    <p className="text-2xl font-bold text-red-600">{stats.totalCancellations}</p>
                    <p className="text-xs text-muted-foreground">booking(s) cancelled</p>
                  </div>
                </div>
              </Card>
              <Card className="p-4">
                <div className="flex items-center gap-3">
                  <div className="p-2 bg-purple-100 rounded-lg">
                    <DollarSign className="h-5 w-5 text-purple-600" />
                  </div>
                  <div>
                    <p className="text-sm text-muted-foreground">Cancelled Value</p>
                    <p className="text-2xl font-bold text-purple-600">{formatPrice(stats.cancellationValue)}</p>
                    <p className="text-xs text-muted-foreground">potential revenue lost</p>
                  </div>
                </div>
              </Card>
              <Card className="p-4">
                <div className="flex items-center gap-3">
                  <div className="p-2 bg-green-100 rounded-lg">
                    <TrendingUp className="h-5 w-5 text-green-600" />
                  </div>
                  <div>
                    <p className="text-sm text-muted-foreground">Net Revenue</p>
                    <p className="text-2xl font-bold text-green-600">{formatPrice(stats.totalRevenue - stats.totalRefunds)}</p>
                    <p className="text-xs text-muted-foreground">after refunds</p>
                  </div>
                </div>
              </Card>
            </div>

            {/* Refunds List */}
            <Card className="p-4">
              <h3 className="font-semibold mb-4 flex items-center gap-2">
                <RefreshCw className="h-5 w-5 text-amber-600" />
                Refund History
              </h3>
              {refundLogs.length === 0 ? (
                <div className="text-center py-8 text-muted-foreground">
                  <RefreshCw className="h-12 w-12 mx-auto mb-3 opacity-30" />
                  <p>No refunds recorded yet</p>
                </div>
              ) : (
                <div className="space-y-3">
                  {refundLogs.map((refund, idx) => (
                    <div key={idx} className="flex items-center justify-between p-3 bg-amber-50 rounded-lg border border-amber-200">
                      <div className="flex items-center gap-3">
                        <div className="p-2 bg-amber-100 rounded-full">
                          <RefreshCw className="h-4 w-4 text-amber-600" />
                        </div>
                        <div>
                          <p className="font-medium text-sm">{refund.booking_id?.substring(0, 8)}...</p>
                          <p className="text-xs text-muted-foreground">
                            {refund.reason?.replace(/_/g, ' ')} • {refund.refund_type === 'full' ? 'Full' : 'Partial'} Refund
                          </p>
                          {refund.admin_notes && (
                            <p className="text-xs text-amber-700 mt-1">Note: {refund.admin_notes}</p>
                          )}
                        </div>
                      </div>
                      <div className="text-right">
                        <p className="font-bold text-amber-700">{formatPrice(refund.amount)}</p>
                        <p className="text-xs text-muted-foreground">{formatDate(refund.created_at)}</p>
                        {refund.stripe_refund_id && (
                          <Badge variant="outline" className="text-xs mt-1">Stripe</Badge>
                        )}
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </Card>

            {/* Cancellations List */}
            <Card className="p-4">
              <h3 className="font-semibold mb-4 flex items-center gap-2">
                <XCircle className="h-5 w-5 text-red-600" />
                Cancellation History
              </h3>
              {cancellationLogs.length === 0 ? (
                <div className="text-center py-8 text-muted-foreground">
                  <XCircle className="h-12 w-12 mx-auto mb-3 opacity-30" />
                  <p>No cancellations recorded yet</p>
                </div>
              ) : (
                <div className="space-y-3">
                  {cancellationLogs.map((cancel, idx) => (
                    <div key={idx} className="flex items-center justify-between p-3 bg-red-50 rounded-lg border border-red-200">
                      <div className="flex items-center gap-3">
                        <div className="p-2 bg-red-100 rounded-full">
                          <XCircle className="h-4 w-4 text-red-600" />
                        </div>
                        <div>
                          <p className="font-medium text-sm">{cancel.tour_title || 'Tour'}</p>
                          <p className="text-xs text-muted-foreground">
                            {cancel.customer_name} • {cancel.reason?.replace(/_/g, ' ')}
                          </p>
                          {cancel.admin_notes && (
                            <p className="text-xs text-red-700 mt-1">Note: {cancel.admin_notes}</p>
                          )}
                        </div>
                      </div>
                      <div className="text-right">
                        <p className="font-bold text-red-700">{formatPrice(cancel.original_amount)}</p>
                        <p className="text-xs text-muted-foreground">{formatDate(cancel.created_at)}</p>
                        <p className="text-xs text-muted-foreground">by {cancel.cancelled_by}</p>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </Card>
          </div>
        </TabsContent>
      </Tabs>

      {/* Quick Payment Dialog */}
      <Dialog open={showQuickPayment} onOpenChange={setShowQuickPayment}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <CreditCard className="h-5 w-5" />
              Quick Payment (Stripe)
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            <div>
              <Label>Customer Name *</Label>
              <Input
                value={quickPaymentForm.customer_name}
                onChange={(e) => setQuickPaymentForm({...quickPaymentForm, customer_name: e.target.value})}
                placeholder="John Doe"
              />
            </div>
            <div>
              <Label>Email</Label>
              <Input
                type="email"
                value={quickPaymentForm.customer_email}
                onChange={(e) => setQuickPaymentForm({...quickPaymentForm, customer_email: e.target.value})}
                placeholder="john@example.com"
              />
            </div>
            <div>
              <Label>Phone</Label>
              <Input
                value={quickPaymentForm.customer_phone}
                onChange={(e) => setQuickPaymentForm({...quickPaymentForm, customer_phone: e.target.value})}
                placeholder="+1 787 555 1234"
              />
            </div>
            <div>
              <Label>Tour (Optional)</Label>
              <Select 
                value={quickPaymentForm.tour_id} 
                onValueChange={(v) => setQuickPaymentForm({...quickPaymentForm, tour_id: v})}
              >
                <SelectTrigger>
                  <SelectValue placeholder="Select tour" />
                </SelectTrigger>
                <SelectContent>
                  {tours.map(t => (
                    <SelectItem key={t.id} value={t.id}>{t.title}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label>Amount *</Label>
                <Input
                  type="number"
                  value={quickPaymentForm.amount}
                  onChange={(e) => setQuickPaymentForm({...quickPaymentForm, amount: e.target.value})}
                  placeholder="125.00"
                />
              </div>
              <div>
                <Label>Guests</Label>
                <Input
                  type="number"
                  value={quickPaymentForm.guests}
                  onChange={(e) => setQuickPaymentForm({...quickPaymentForm, guests: parseInt(e.target.value) || 1})}
                  min="1"
                />
              </div>
            </div>
            <div>
              <Label>Tour Date</Label>
              <Input
                type="date"
                value={quickPaymentForm.tour_date}
                onChange={(e) => setQuickPaymentForm({...quickPaymentForm, tour_date: e.target.value})}
              />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowQuickPayment(false)}>Cancel</Button>
            <Button onClick={handleQuickPayment} className="gap-2">
              <ExternalLink className="h-4 w-4" />
              Open Stripe Checkout
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Manual Payment Dialog */}
      <Dialog open={showManualPayment} onOpenChange={setShowManualPayment}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Banknote className="h-5 w-5" />
              Record Manual Payment
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            <div>
              <Label>Payment Method *</Label>
              <Select 
                value={manualPaymentForm.payment_method} 
                onValueChange={(v) => setManualPaymentForm({...manualPaymentForm, payment_method: v})}
              >
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {PAYMENT_METHODS.filter(m => m.value !== 'stripe').map(m => (
                    <SelectItem key={m.value} value={m.value}>
                      <span className="flex items-center gap-2">
                        <m.icon className="h-4 w-4" />
                        {m.label}
                      </span>
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label>Customer Name *</Label>
              <Input
                value={manualPaymentForm.customer_name}
                onChange={(e) => setManualPaymentForm({...manualPaymentForm, customer_name: e.target.value})}
                placeholder="John Doe"
              />
            </div>
            <div>
              <Label>Email</Label>
              <Input
                type="email"
                value={manualPaymentForm.customer_email}
                onChange={(e) => setManualPaymentForm({...manualPaymentForm, customer_email: e.target.value})}
              />
            </div>
            <div>
              <Label>Tour (Optional)</Label>
              <Select 
                value={manualPaymentForm.tour_id} 
                onValueChange={(v) => setManualPaymentForm({...manualPaymentForm, tour_id: v})}
              >
                <SelectTrigger>
                  <SelectValue placeholder="Select tour" />
                </SelectTrigger>
                <SelectContent>
                  {tours.map(t => (
                    <SelectItem key={t.id} value={t.id}>{t.title}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label>Amount *</Label>
                <Input
                  type="number"
                  value={manualPaymentForm.amount}
                  onChange={(e) => setManualPaymentForm({...manualPaymentForm, amount: e.target.value})}
                  placeholder="125.00"
                />
              </div>
              <div>
                <Label>Reference #</Label>
                <Input
                  value={manualPaymentForm.reference_number}
                  onChange={(e) => setManualPaymentForm({...manualPaymentForm, reference_number: e.target.value})}
                  placeholder="Optional"
                />
              </div>
            </div>
            <div>
              <Label>Notes</Label>
              <Textarea
                value={manualPaymentForm.notes}
                onChange={(e) => setManualPaymentForm({...manualPaymentForm, notes: e.target.value})}
                placeholder="Any notes about this payment..."
                rows={2}
              />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowManualPayment(false)}>Cancel</Button>
            <Button onClick={handleAddManualPayment}>Record Payment</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Create Invoice Dialog */}
      <Dialog open={showCreateInvoice} onOpenChange={setShowCreateInvoice}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <FileText className="h-5 w-5" />
              Create Invoice
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            <div>
              <Label>Customer Name *</Label>
              <Input
                value={invoiceForm.customer_name}
                onChange={(e) => setInvoiceForm({...invoiceForm, customer_name: e.target.value})}
                placeholder="John Doe"
              />
            </div>
            <div>
              <Label>Email *</Label>
              <Input
                type="email"
                value={invoiceForm.customer_email}
                onChange={(e) => setInvoiceForm({...invoiceForm, customer_email: e.target.value})}
                placeholder="john@example.com"
              />
            </div>
            <div>
              <Label>Phone</Label>
              <Input
                value={invoiceForm.customer_phone}
                onChange={(e) => setInvoiceForm({...invoiceForm, customer_phone: e.target.value})}
              />
            </div>
            <div>
              <Label>Tour</Label>
              <Select 
                value={invoiceForm.tour_id} 
                onValueChange={(v) => setInvoiceForm({...invoiceForm, tour_id: v})}
              >
                <SelectTrigger>
                  <SelectValue placeholder="Select tour" />
                </SelectTrigger>
                <SelectContent>
                  {tours.map(t => (
                    <SelectItem key={t.id} value={t.id}>{t.title}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label>Amount *</Label>
                <Input
                  type="number"
                  value={invoiceForm.amount}
                  onChange={(e) => setInvoiceForm({...invoiceForm, amount: e.target.value})}
                  placeholder="125.00"
                />
              </div>
              <div>
                <Label>Due Date</Label>
                <Input
                  type="date"
                  value={invoiceForm.due_date}
                  onChange={(e) => setInvoiceForm({...invoiceForm, due_date: e.target.value})}
                />
              </div>
            </div>
            <div>
              <Label>Notes</Label>
              <Textarea
                value={invoiceForm.notes}
                onChange={(e) => setInvoiceForm({...invoiceForm, notes: e.target.value})}
                placeholder="Invoice notes..."
                rows={2}
              />
            </div>
          </div>
          <DialogFooter className="gap-2 sm:gap-0">
            <Button variant="outline" onClick={() => setShowCreateInvoice(false)}>Cancel</Button>
            <Button variant="secondary" onClick={() => handleCreateInvoice(false)}>Create Only</Button>
            <Button onClick={() => handleCreateInvoice(true)} className="gap-2">
              <Send className="h-4 w-4" />
              Create & Email Invoice
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Payment Details Dialog */}
      <Dialog open={!!showPaymentDetails} onOpenChange={() => setShowPaymentDetails(null)}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle>Payment Details</DialogTitle>
          </DialogHeader>
          {showPaymentDetails && (
            <div className="space-y-4">
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label className="text-muted-foreground">Customer</Label>
                  <p className="font-semibold text-lg">{showPaymentDetails.customer_name || showPaymentDetails.booker_name}</p>
                  <p className="text-sm">{showPaymentDetails.customer_email || showPaymentDetails.booker_email}</p>
                  <p className="text-sm">{showPaymentDetails.customer_phone || showPaymentDetails.booker_phone}</p>
                </div>
                <div>
                  <Label className="text-muted-foreground">Amount</Label>
                  <p className="font-bold text-2xl text-green-600">
                    {formatPrice(showPaymentDetails.amount || showPaymentDetails.total_price)}
                  </p>
                </div>
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label className="text-muted-foreground">Tour</Label>
                  <p className="font-medium">{showPaymentDetails.tour_title}</p>
                </div>
                <div>
                  <Label className="text-muted-foreground">Guests</Label>
                  <p>{showPaymentDetails.guests}</p>
                </div>
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label className="text-muted-foreground">Tour Date</Label>
                  <p>{formatDate(showPaymentDetails.tour_date || showPaymentDetails.date)}</p>
                </div>
                <div>
                  <Label className="text-muted-foreground">Payment Date</Label>
                  <p>{formatDateTime(showPaymentDetails.paid_at || showPaymentDetails.created_at)}</p>
                </div>
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label className="text-muted-foreground">Status</Label>
                  <Badge variant={showPaymentDetails.payment_status === 'paid' ? 'default' : 'secondary'}>
                    {showPaymentDetails.payment_status}
                  </Badge>
                </div>
                <div>
                  <Label className="text-muted-foreground">Method</Label>
                  <Badge className={PAYMENT_METHODS.find(m => m.value === (showPaymentDetails.payment_method || 'stripe'))?.color}>
                    {PAYMENT_METHODS.find(m => m.value === (showPaymentDetails.payment_method || 'stripe'))?.label}
                  </Badge>
                </div>
              </div>
              {showPaymentDetails.booking_id && (
                <div>
                  <Label className="text-muted-foreground">Booking ID</Label>
                  <p className="font-mono text-sm">{showPaymentDetails.booking_id}</p>
                </div>
              )}
              {showPaymentDetails.notes && (
                <div>
                  <Label className="text-muted-foreground">Notes</Label>
                  <p className="text-sm">{showPaymentDetails.notes}</p>
                </div>
              )}
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* Edit Message Dialog */}
      <Dialog open={showEditMessage} onOpenChange={setShowEditMessage}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              {messageContent.type === 'email' ? <Mail className="h-5 w-5" /> : <MessageSquare className="h-5 w-5" />}
              Edit {messageContent.type === 'email' ? 'Email' : 'SMS'} Before Sending
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            {messageContent.type === 'email' && (
              <div>
                <Label>Subject</Label>
                <Input
                  value={messageContent.subject}
                  onChange={(e) => setMessageContent({...messageContent, subject: e.target.value})}
                />
              </div>
            )}
            <div>
              <Label>{messageContent.type === 'email' ? 'Message Body' : 'Message'}</Label>
              <Textarea
                value={messageContent.body}
                onChange={(e) => setMessageContent({...messageContent, body: e.target.value})}
                rows={messageContent.type === 'email' ? 8 : 4}
              />
            </div>
            <div className="text-sm text-muted-foreground">
              Sending to: {messageContent.item?.customer_email || messageContent.item?.customer_phone}
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowEditMessage(false)}>Cancel</Button>
            <Button onClick={sendEditedMessage} className="gap-2">
              <Send className="h-4 w-4" />
              Send
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Refund Dialog */}
      <Dialog open={showRefundDialog} onOpenChange={setShowRefundDialog}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 text-red-600">
              <XCircle className="h-5 w-5" />
              Process Refund
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            <p>To process a refund for this payment, you&apos;ll need to use the Stripe Dashboard.</p>
            {refundItem && (
              <Card className="p-4 bg-muted/50">
                <p><strong>Customer:</strong> {refundItem.customer_name}</p>
                <p><strong>Amount:</strong> {formatPrice(refundItem.amount || refundItem.total_price)}</p>
                <p><strong>Tour:</strong> {refundItem.tour_title}</p>
              </Card>
            )}
            <p className="text-sm text-muted-foreground">
              Click below to open the Stripe Dashboard where you can search for this payment and process the refund.
            </p>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowRefundDialog(false)}>Cancel</Button>
            <Button onClick={openStripeRefund} className="gap-2 bg-red-600 hover:bg-red-700">
              <ExternalLink className="h-4 w-4" />
              Open Stripe Dashboard
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Profit Report Dialog */}
      <Dialog open={showProfitReport} onOpenChange={setShowProfitReport}>
        <DialogContent className="max-w-2xl">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <BarChart3 className="h-5 w-5" />
              Profit Report
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-6">
            {/* Report Filters */}
            <div className="flex flex-wrap gap-4">
              <div>
                <Label>From Date</Label>
                <Input
                  type="date"
                  value={reportFilters.dateFrom}
                  onChange={(e) => setReportFilters({...reportFilters, dateFrom: e.target.value})}
                />
              </div>
              <div>
                <Label>To Date</Label>
                <Input
                  type="date"
                  value={reportFilters.dateTo}
                  onChange={(e) => setReportFilters({...reportFilters, dateTo: e.target.value})}
                />
              </div>
              <div>
                <Label>Tour</Label>
                <Select 
                  value={reportFilters.tourId} 
                  onValueChange={(v) => setReportFilters({...reportFilters, tourId: v})}
                >
                  <SelectTrigger className="w-48">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">All Tours</SelectItem>
                    {tours.map(t => (
                      <SelectItem key={t.id} value={t.id}>{t.title}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div className="flex items-end">
                <Button onClick={generateProfitReport}>Generate Report</Button>
              </div>
            </div>

            {/* Report Results */}
            {reportData && (
              <div className="space-y-6">
                <div className="text-sm text-muted-foreground">
                  Report Period: {reportData.dateRange.from} to {reportData.dateRange.to}
                </div>

                {/* Summary Cards */}
                <div className="grid grid-cols-3 gap-4">
                  <Card className="p-4 bg-green-50">
                    <p className="text-sm text-muted-foreground">Total Revenue</p>
                    <p className="text-2xl font-bold text-green-600">{formatPrice(reportData.totalRevenue)}</p>
                  </Card>
                  <Card className="p-4 bg-blue-50">
                    <p className="text-sm text-muted-foreground">Transactions</p>
                    <p className="text-2xl font-bold text-blue-600">{reportData.totalTransactions}</p>
                  </Card>
                  <Card className="p-4 bg-red-50">
                    <p className="text-sm text-muted-foreground">Cancellations</p>
                    <p className="text-2xl font-bold text-red-600">{reportData.cancellations}</p>
                    <p className="text-sm text-red-500">{formatPrice(reportData.cancellationValue)} lost</p>
                  </Card>
                </div>

                {/* By Payment Method */}
                <div>
                  <h4 className="font-semibold mb-3">Revenue by Payment Method</h4>
                  <div className="space-y-2">
                    {PAYMENT_METHODS.map(m => (
                      <div key={m.value} className="flex items-center justify-between p-3 bg-muted/50 rounded-lg">
                        <div className="flex items-center gap-2">
                          <Badge className={m.color}>{m.label}</Badge>
                        </div>
                        <span className="font-semibold">{formatPrice(reportData.byMethod[m.value] || 0)}</span>
                      </div>
                    ))}
                  </div>
                </div>

                {/* By Tour */}
                <div>
                  <h4 className="font-semibold mb-3">Revenue by Tour</h4>
                  <div className="space-y-2 max-h-48 overflow-y-auto">
                    {Object.entries(reportData.byTour).sort((a, b) => b[1] - a[1]).map(([tour, amount]) => (
                      <div key={tour} className="flex items-center justify-between p-3 bg-muted/50 rounded-lg">
                        <span>{tour}</span>
                        <span className="font-semibold">{formatPrice(amount)}</span>
                      </div>
                    ))}
                  </div>
                </div>
              </div>
            )}
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
